import type { MediaAsset, MediaKind } from "../domain/types";
import {
  ID_MAX_BYTES,
  ID_MIME_OK,
  PHOTO_MAX_BYTES,
  PHOTO_MIME_OK,
  VIDEO_MAX_BYTES,
  VIDEO_MIME_OK,
} from "./limits";
import { uploadMediaAsset } from "./uploadService";

function uid(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

export class MediaValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MediaValidationError";
  }
}

function validate(kind: MediaKind, file: File): void {
  if (kind === "government_id") {
    if (!ID_MIME_OK.test(file.type) && !/\.pdf$/i.test(file.name)) {
      throw new MediaValidationError("Government ID must be an image or PDF.");
    }
    if (file.size > ID_MAX_BYTES) {
      throw new MediaValidationError("Government ID file is too large (max 12 MB).");
    }
    return;
  }
  if (kind === "confirmation_video") {
    if (!VIDEO_MIME_OK.test(file.type)) {
      throw new MediaValidationError("Confirmation must be a video file.");
    }
    if (file.size > VIDEO_MAX_BYTES) {
      throw new MediaValidationError("Video is too large (max 50 MB).");
    }
    return;
  }
  if (!PHOTO_MIME_OK.test(file.type)) {
    throw new MediaValidationError("Profile photos must be images.");
  }
  if (file.size > PHOTO_MAX_BYTES) {
    throw new MediaValidationError("Photo is too large (max 12 MB).");
  }
}

/**
 * Session-only asset (blob preview, uploadStatus pending).
 * Prefer createAndPersistMediaAsset so review survives reload.
 */
export function createMediaAsset(kind: MediaKind, file: File): MediaAsset {
  validate(kind, file);
  const objectUrl = URL.createObjectURL(file);
  return {
    id: uid(kind),
    kind,
    fileName: file.name || `${kind}.bin`,
    mimeType: file.type || "application/octet-stream",
    sizeBytes: file.size,
    objectUrl,
    storageKey: null,
    publicUrl: null,
    uploadStatus: "pending",
    uploadSource: null,
    uploadError: null,
    reviewStatus: "pending_review",
    createdAt: new Date().toISOString(),
    reviewNote: null,
  };
}

/**
 * Validate → session blob → durable storage via active MediaUploadAdapter.
 * Local prototype: IndexedDB under storageKey. Cloud: set publicUrl to CDN.
 */
export async function createAndPersistMediaAsset(
  kind: MediaKind,
  file: File,
  userId: string
): Promise<MediaAsset> {
  const draft = createMediaAsset(kind, file);
  return uploadMediaAsset(draft, file, userId || "anonymous");
}

/** Alias used by capture UI. */
export const createAndUploadMedia = createAndPersistMediaAsset;

export function revokeMediaAsset(asset: MediaAsset | null | undefined): void {
  if (asset?.objectUrl?.startsWith("blob:")) {
    URL.revokeObjectURL(asset.objectUrl);
  }
}

export function revokeMany(assets: (MediaAsset | null | undefined)[]): void {
  for (const a of assets) revokeMediaAsset(a);
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** True when asset has durable storageKey and successful upload. */
export function hasDurableMediaRef(asset: MediaAsset | null | undefined): boolean {
  return Boolean(asset?.storageKey && asset.uploadStatus === "uploaded");
}
